import { Canvas } from "@react-three/fiber";
import { Plane } from "@react-three/drei";
import { DoubleSide } from "three";
import { useViewSettings } from "@/view-settings";
import { useObserveEffect, useSelector } from "@legendapp/state/react";
import {
  useDetectionsBuffer,
  useReprojectionsBuffer,
} from "@/services/buffer-controller";
import { useRef } from "react";
import Zoomist from "zoomist";
import { MetaInfo } from "./meta-info";
import { CameraViewImage } from "./camera-view-image";
import { Detections } from "./detections";
import { Reprojections } from "./reprojections";

export function Canvas2D({ debug = false }) {
  const detectionsBuffer = useDetectionsBuffer();
  const projectionsBuffer = useReprojectionsBuffer();
  const viewSettings = useViewSettings();

  const isVisible = useSelector(
    () => viewSettings.viewMode.get() === "detections"
  );
  const isDetectionsVisible = useSelector(() =>
    viewSettings.showDetections.get()
  );
  const isReprojectionsVisible = useSelector(() =>
    viewSettings.showReprojections.get()
  );
  const containerRef = useRef<HTMLDivElement>(null);

  useObserveEffect(() => {
    const isVisible = viewSettings.viewMode.get() === "detections";

    if (isVisible) {
      setTimeout(() => {
        if (!containerRef.current) return;

        new Zoomist(containerRef.current, {
          maxScale: 10,
          bounds: true,
          zoomRatio: 0.15,
        });
      });
    }

    if (!isVisible) {
      detectionsBuffer.destroy();
      projectionsBuffer.destroy();
    }
  });

  if (!isVisible) {
    return null;
  }

  return (
    <div ref={containerRef} className="zoomist-container">
      <div className="zoomist-wrapper">
        <div className="zoomist-image aspect-video">
          <div className="py-20 relative bg-black overflow-hidden w-full h-full">
            <MetaInfo />
            <Canvas
              key="canvas-2d"
              id="canvas-2d"
              className="py-20"
              gl={{ alpha: true }}
              camera={{ position: [0, 5.87, 0] }}
              raycaster={{ params: { Points: { threshold: 0.1 } } }}
            >
              {debug && <axesHelper args={[10]} rotation={[Math.PI, 0, 0]} />}
              <group position={[0, 0, 0]} rotation={[Math.PI, 0, 0]}>
                {debug && (
                  <Plane args={[16, 9]} rotation={[Math.PI / 2, 0, 0]}>
                    <meshBasicMaterial
                      side={DoubleSide}
                      color="blue"
                      opacity={0.5}
                      transparent
                    />
                  </Plane>
                )}
                {isDetectionsVisible && <Detections />}
                {isReprojectionsVisible && <Reprojections />}
              </group>
            </Canvas>
            <CameraViewImage />
          </div>
        </div>
      </div>
    </div>
  );
}
